import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useParams, Link } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { getSite, getBlogPost } from '../../services/CommonServices';
import GlobalHeader from '../GlobalHeader';
import GlobalFooter from '../GlobalFooter';
import { ShadowContainer, NoShadowContainer } from '../Layout';
import { useInstance } from '../../contexts/InstanceContext';

// Outer wrapper with gray background - uses calc to account for Header navbar (40px)
const PageWrapper = styled.div`
  background-color: #f9f9f9;
  min-height: calc(100dvh - 40px);
  display: flex;
  flex-direction: column;
`;

// Simple wrapper - layout handled by ShadowContainer/NoShadowContainer
const SiteWrapper = styled.div`
  transition: all 0.3s;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

// Main content area that grows to push footer down
const MainContent = styled.main`
  flex: 1;
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 20px 60px;

  @media (max-width: 768px) {
    padding: 24px 16px 40px;
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  margin-bottom: 24px;
  color: ${props => props.theme.colors?.primaryColor || '#c41e3a'};
  text-decoration: none;
  font-size: 0.95rem;
  font-weight: 500;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }

  &::before {
    content: '←';
    margin-right: 8px;
    font-size: 1.1rem;
  }

  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 20px;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    margin-bottom: 24px;
  }
`;

const Title = styled.h1`
  font-size: 2.2rem;
  font-weight: 700;
  line-height: 1.4;
  margin-bottom: 16px;
  color: #222;
  word-break: keep-all;

  @media (max-width: 768px) {
    font-size: 1.6rem;
    margin-bottom: 12px;
  }
`;

const Meta = styled.div`
  color: #888;
  font-size: 0.95rem;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const Thumbnail = styled.img`
  width: 100%;
  max-height: 450px;
  object-fit: cover;
  border-radius: 12px;
  margin-bottom: 40px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    max-height: 280px;
    border-radius: 8px;
    margin-bottom: 28px;
  }
`;

const Content = styled.div`
  line-height: 1.9;
  font-size: 1.1rem;
  color: #333;

  img {
    max-width: 100%;
    height: auto;
    margin: 28px 0;
    border-radius: 8px;
  }

  h2 {
    font-size: 1.6rem;
    font-weight: 600;
    margin-top: 48px;
    margin-bottom: 20px;
    color: #222;

    @media (max-width: 768px) {
      font-size: 1.3rem;
      margin-top: 36px;
      margin-bottom: 16px;
    }
  }

  h3 {
    font-size: 1.3rem;
    font-weight: 600;
    margin-top: 36px;
    margin-bottom: 16px;
    color: #333;

    @media (max-width: 768px) {
      font-size: 1.15rem;
      margin-top: 28px;
      margin-bottom: 12px;
    }
  }

  p {
    margin-bottom: 24px;

    @media (max-width: 768px) {
      margin-bottom: 20px;
    }
  }

  ul, ol {
    margin-bottom: 24px;
    padding-left: 24px;

    li {
      margin-bottom: 8px;
    }
  }

  blockquote {
    border-left: 4px solid ${props => props.theme.colors?.primaryColor || '#c41e3a'};
    margin: 28px 0;
    padding: 16px 24px;
    background: #f9f9f9;
    border-radius: 0 8px 8px 0;
    font-style: italic;
    color: #555;
  }

  a {
    color: ${props => props.theme.colors?.primaryColor || '#c41e3a'};
    text-decoration: underline;
    text-underline-offset: 2px;

    &:hover {
      opacity: 0.8;
    }
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.8;
  }
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: #666;
  font-size: 1rem;
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
`;

const BlogPost = (props) => {
  const { site, globalHeaderType, globalFooterType } = props;
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const intl = useIntl();
  const { memberId } = useInstance();

  useEffect(() => {
    if (!memberId) return;

    const fetchData = async () => {
      try {
        const siteRes = await getSite(memberId);

        if (siteRes.data.success) {
          const siteId = siteRes.data.data.id;
          const postRes = await getBlogPost(siteId, slug);

          if (postRes.data.success) {
            setPost(postRes.data.data);
          } else {
            setError('post_not_found');
          }
        } else {
          setError('site_not_found');
        }
      } catch (err) {
        console.error(err);
        setError('error_loading');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [memberId, slug]);

  // Choose wrapper based on hasBackground setting
  const ContentWrapper = site?.hasBackground ? ShadowContainer : NoShadowContainer;

  const renderWithLayout = (content) => (
    <PageWrapper>
      <ContentWrapper hasShadow={site?.hasBackground}>
        <SiteWrapper className="App">
          <GlobalHeader site={site} headerType={globalHeaderType} />
          <MainContent>
            <Container>{content}</Container>
          </MainContent>
          <GlobalFooter site={site} footerType={globalFooterType} />
        </SiteWrapper>
      </ContentWrapper>
    </PageWrapper>
  );

  if (loading) {
    return renderWithLayout(
      <LoadingContainer>
        <FormattedMessage id="blog.loading" defaultMessage="Loading..." />
      </LoadingContainer>
    );
  }

  if (error) {
    return renderWithLayout(
      <ErrorContainer>
        <BackLink to="/blog">
          <FormattedMessage id="blog.back_to_list" defaultMessage="Back to Blog" />
        </BackLink>
        <p>
          <FormattedMessage id={`blog.${error}`} defaultMessage={error} />
        </p>
      </ErrorContainer>
    );
  }

  if (!post) {
    return renderWithLayout(
      <ErrorContainer>
        <BackLink to="/blog">
          <FormattedMessage id="blog.back_to_list" defaultMessage="Back to Blog" />
        </BackLink>
        <p>
          <FormattedMessage id="blog.post_not_found" defaultMessage="Post not found" />
        </p>
      </ErrorContainer>
    );
  }

  return renderWithLayout(
    <>
      <BackLink to="/blog">
        <FormattedMessage id="blog.back_to_list" defaultMessage="Back to Blog" />
      </BackLink>
      <Header>
        <Title>{post.title}</Title>
        <Meta>
          {new Date(post.created_at).toLocaleDateString(intl.locale, {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
        </Meta>
      </Header>

      {post.thumbnail && <Thumbnail src={post.thumbnail} alt={post.title} />}

      <Content dangerouslySetInnerHTML={{ __html: post.content }} />
    </>
  );
};

export default BlogPost;
